import React, { useEffect, useMemo, useState } from "react";
import { useAppDispatch } from "hooks/redux";
import { useTranslation } from "react-i18next";

import { GetPrintCartSigns } from "api/rpc/2024-04/facilityAdmin/teesheet/teeSheet";
import { StatusCode } from "api/protocols";

import { showError } from "redux/actions/ui";

import Sheet from "components/sheet/Sheet";
import { Select } from "components/select/index";
import TimePick from "components/timePick/TimePick";
import Divider from "components/divider";
import CartOption1Preview from "../elements/CartOption1Preview";
import CartOption2Preview from "../elements/CartOption2Preview";
import CartOption3Preview from "../elements/CartOption3Preview";

import { ITeeTimeNew } from "redux/reducers/models/teetime";
import { ITournament } from "redux/reducers/models/tournament";
import { ILeague } from "redux/reducers/models/league";

import "./printCartSignsModal.scss";

interface IPrintCartSignsModal {
  open: boolean;
  teetime: ITeeTimeNew;
  teeTimes?: ITeeTimeNew[];
  tournaments?: ITournament[];
  leagues?: ILeague[];
  teetime_id?: number;
  onOk: () => void;
  onCancel: () => void;
}

function getPrintableTeeTimes(selected: ITeeTimeNew, teeTimes: ITeeTimeNew[] = []): ITeeTimeNew[] {
  const slot = selected?.slots?.[0];
  if (!slot || teeTimes.length === 0) {
    return selected ? [selected] : [];
  }

  let filtered: ITeeTimeNew[] = [];

  if (slot.tournament_id) {
    filtered = teeTimes.filter(
      t =>
        t.slots?.[0]?.tournament_id === slot.tournament_id &&
        t.slots?.[0]?.shotgun_application_id === slot.shotgun_application_id,
    );
  } else if (slot.league_id) {
    filtered = teeTimes.filter(
      t =>
        t.slots?.[0]?.league_id === slot.league_id &&
        t.slots?.[0]?.shotgun_application_id === slot.shotgun_application_id,
    );
  } else if (slot.shotgun_application_id) {
    filtered = teeTimes.filter(t => t.slots?.[0]?.shotgun_application_id === slot.shotgun_application_id);
  } else {
    filtered = teeTimes.filter(t => !t.slots?.[0]?.tournament_id && !t.slots?.[0]?.league_id);
  }

  return filtered.length > 0 ? filtered : [selected];
}

function getTimeRangeDefaults(teeTimes: ITeeTimeNew[], fallbackStartTime?: string) {
  const times = teeTimes.map(t => t.start_time).filter(Boolean).sort();

  if (times.length === 0) {
    return {
      startTime: fallbackStartTime ?? "00:00",
      endTime: fallbackStartTime ?? "23:59",
    };
  }

  return {
    startTime: times[0],
    endTime: times[times.length - 1],
  };
}

export default function PrintCartSignsModal(props: IPrintCartSignsModal) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [option, setOption] = useState<number>(1);
  const [event, setEvent] = useState<ITournament | ILeague | null>(undefined);

  const printableTeeTimes = useMemo(
    () => getPrintableTeeTimes(props.teetime, props.teeTimes),
    [props.teetime, props.teeTimes],
  );

  const defaultRange = useMemo(
    () => getTimeRangeDefaults(printableTeeTimes, props.teetime?.start_time),
    [printableTeeTimes, props.teetime?.start_time],
  );

  const [startTime, setStartTime] = useState<string>(defaultRange.startTime);
  const [endTime, setEndTime] = useState<string>(defaultRange.endTime);

  useEffect(() => {
    setCartSignEvent();
  }, []);

  useEffect(() => {
    setStartTime(defaultRange.startTime);
    setEndTime(defaultRange.endTime);
  }, [defaultRange.startTime, defaultRange.endTime]);

  function setCartSignEvent() {
    let event = null;

    if (props.teetime.slots[0].tournament_id) {
      event = props.tournaments?.find(tournament => tournament.id === props.teetime.slots[0].tournament_id);
    } else if (props.teetime.slots[0].league_id) {
      event = props.leagues?.find(league => league.id === props.teetime.slots[0].league_id);
    }

    setEvent(event);
  }

  async function printCartSigns() {
    const cartSignsRes = await GetPrintCartSigns(
      {
        date: props.teetime.slots[0].date,
        league_id: props.teetime.slots[0].league_id,
        tournament_id: props.teetime.slots[0].tournament_id,
        shotgun_application_id: props.teetime.slots[0].shotgun_application_id,
        start_time: startTime,
        end_time: endTime,
        option: option,
      },
      true,
    );

    if (cartSignsRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error printing cart signs")));
      return;
    }

    window.open().document.write(String(cartSignsRes.data));
  }

  return (
    <Sheet
      open={props.open}
      title={t("client_side.tee_sheet.new_tee_sheet.modals.print_cart_signs_modal.001")}
      size="modal"
      onOk={() => {
        void printCartSigns();
        props.onOk();
      }}
      onCancel={props.onCancel}
      okText={t("client_side.tee_sheet.new_tee_sheet.modals.print_cart_signs_modal.002")}
    >
      <div className="print-cart-signs-modal">
        <div className="mb-2">
          <Select
            label={t("Layout")}
            onChange={value => setOption(Number(value))}
            defaultValue={1}
            usePortal
          >
            <Select.Option value={1}>{t("Option 1")}</Select.Option>
            {/* <Select.Option value={2}>{"Option 2"}</Select.Option> */}
            <Select.Option value={3}>{t("Option 2")}</Select.Option>
          </Select>
        </div>
        {props.tournaments && option === 1 ? <CartOption1Preview teetime={props.teetime} event={event} /> : null}
        {props.tournaments && option === 2 ? <CartOption2Preview teetime={props.teetime} event={event} /> : null}
        {props.tournaments && option === 3 ? <CartOption3Preview teetime={props.teetime} event={event} /> : null}
        <div className="cart-sign-time-range">
          <Divider />
          <div className="cart-sign-time-range-title">{t("Time Picker")}</div>
          <div className="cart-sign-time-range-pickers">
            <TimePick
              label={t("From")}
              value={startTime}
              onChange={timeString => setStartTime(timeString ?? startTime)}
              status={startTime > endTime ? "warning" : undefined}
              positionTop
            />
            <TimePick
              label={t("To")}
              value={endTime}
              onChange={timeString => setEndTime(timeString ?? endTime)}
              status={startTime > endTime ? "warning" : undefined}
              align="right"
              positionTop
            />
          </div>
          <p className="cart-sign-time-range-note">
            {t("Defaults to all tee times. Narrow the range to print fewer cart signs.")}
          </p>
        </div>
      </div>
    </Sheet>
  );
}
